import classNames from "classnames";
import { KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import * as Flags from "country-flag-icons/react/1x1";
import type { Country } from "react-phone-number-input/input";
import { ReactComponent as ArrowIcon } from "@icons/arrow.svg";
import useComponentVisible from "../Hooks/useComponentVisible";
import { countryOptions } from "./countries";

export const COUNTRY_LIST_MAX_HEIGHT_PX = 288;
export const TYPEAHEAD_RESET_MS = 500;
const PAGE_JUMP = 10;

type CountrySelectorProps = {
  country: Country;
  onChange: (country: Country) => void;
  label: string;
  hasError: boolean;
};

const isPrintableKey = (e: KeyboardEvent) => e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

const spaceBelow = (trigger: HTMLElement) => {
  const viewport = window.visualViewport;
  const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
  return visibleBottom - trigger.getBoundingClientRect().bottom;
};

export const CountrySelector = ({ country, onChange, label, hasError }: CountrySelectorProps) => {
  const listboxId = useId();
  const optionIdPrefix = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeahead = useRef<{ text: string; timer?: ReturnType<typeof setTimeout> }>({ text: "" });
  const { targetAreaRef, isComponentVisible: isOpen, setIsComponentVisible: setIsOpen } = useComponentVisible<HTMLDivElement>(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [opensUpwards, setOpensUpwards] = useState(false);

  const selectedIndex = Math.max(
    0,
    countryOptions.findIndex((option) => option.country === country)
  );
  const selected = countryOptions[selectedIndex];
  const lastIndex = countryOptions.length - 1;
  const SelectedFlag = Flags[country as keyof typeof Flags];
  const optionId = (index: number) => `${optionIdPrefix}-${index}`;

  useEffect(() => {
    if (isOpen) {
      listRef.current?.children[activeIndex]?.scrollIntoView?.({ block: "nearest" });
    }
  }, [isOpen, activeIndex]);

  useEffect(() => () => clearTimeout(typeahead.current.timer), []);

  const open = (index = selectedIndex) => {
    if (triggerRef.current) {
      triggerRef.current.focus();
      const below = spaceBelow(triggerRef.current);
      setOpensUpwards(below < COUNTRY_LIST_MAX_HEIGHT_PX && triggerRef.current.getBoundingClientRect().top > below);
    }
    setActiveIndex(index);
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  const select = (index: number) => {
    close();
    triggerRef.current?.focus();
    const option = countryOptions[index];
    if (option.country !== country) {
      onChange(option.country);
    }
  };

  const findTypeaheadMatch = (key: string, fromIndex: number) => {
    clearTimeout(typeahead.current.timer);
    const text = typeahead.current.text + key.toLowerCase();
    typeahead.current = { text, timer: setTimeout(() => (typeahead.current.text = ""), TYPEAHEAD_RESET_MS) };

    const isRepeatedLetter = text.split("").every((char) => char === text[0]);
    const search = isRepeatedLetter ? text[0] : text;
    const start = isRepeatedLetter ? fromIndex + 1 : fromIndex;
    const ordered = [...countryOptions.slice(start), ...countryOptions.slice(0, start)];
    const match = ordered.find((option) => option.name.toLowerCase().startsWith(search));
    return match ? countryOptions.indexOf(match) : undefined;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === " ") {
        e.preventDefault();
        open();
      } else if (isPrintableKey(e)) {
        e.preventDefault();
        open(findTypeaheadMatch(e.key, selectedIndex) ?? selectedIndex);
      }
      return;
    }

    const moveBy = (step: number) => {
      e.preventDefault();
      setActiveIndex((index) => Math.min(lastIndex, Math.max(0, index + step)));
    };

    switch (e.key) {
      case "ArrowDown":
        return moveBy(1);
      case "ArrowUp":
        return moveBy(-1);
      case "PageDown":
        return moveBy(PAGE_JUMP);
      case "PageUp":
        return moveBy(-PAGE_JUMP);
      case "Home":
        return moveBy(-lastIndex);
      case "End":
        return moveBy(lastIndex);
      case "Enter":
        e.preventDefault();
        return select(activeIndex);
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        return close();
      case "Tab":
        return close();
    }

    if (e.key === " " && !typeahead.current.text) {
      e.preventDefault();
      return select(activeIndex);
    }

    if (isPrintableKey(e)) {
      e.preventDefault();
      const match = findTypeaheadMatch(e.key, activeIndex);
      if (match !== undefined) {
        setActiveIndex(match);
      }
    }
  };

  return (
    <div ref={targetAreaRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-label={`${label}: ${selected.name} (${selected.dialCode})`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={isOpen ? optionId(activeIndex) : undefined}
        aria-invalid={hasError || undefined}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleKeyDown}
        onKeyUp={(e) => e.key === " " && e.preventDefault()}
        className={classNames(
          "flex h-[58px] w-[90px] items-center gap-2 rounded-[10px] bg-l-blue px-4",
          hasError ? "outline outline-2 outline-brand-red" : "focus:outline focus:outline-2 focus:outline-navy-blue"
        )}
      >
        <span aria-hidden="true" className="inline-flex h-9 w-9 overflow-hidden rounded-full">
          {SelectedFlag && <SelectedFlag data-testid={`flag-${country}`} className="h-full w-full" />}
        </span>
        <ArrowIcon aria-hidden="true" width={14} height={14} className={classNames("transition-transform", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={label}
          tabIndex={-1}
          style={{ maxHeight: COUNTRY_LIST_MAX_HEIGHT_PX }}
          className={classNames(
            "absolute left-0 z-20 w-72 max-w-[calc(100vw-2rem)] list-none overflow-y-auto rounded-[6px] bg-l-blue py-2 pl-0 shadow-md",
            opensUpwards ? "bottom-full mb-2" : "top-full mt-2"
          )}
        >
          {countryOptions.map((option, index) => {
            const isSelected = option.country === country;
            return (
              <li
                key={option.country}
                id={optionId(index)}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => e.preventDefault()}
                onMouseMove={() => index !== activeIndex && setActiveIndex(index)}
                onClick={() => select(index)}
                onKeyDown={(e) => e.key === "Enter" && select(index)}
                className={classNames(
                  "cursor-pointer px-4 py-1.5",
                  isSelected ? "bg-navy-blue font-bold text-white" : "text-navy-blue",
                  index === activeIndex && "outline outline-2 -outline-offset-2 outline-navy-blue"
                )}
              >
                {option.name} ({option.dialCode})
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
